import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { prefersReducedMotion } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/**
 * Artyeto product page blocks (blocks/artyeto-*.liquid).
 *
 * The Liquid markup is the source of truth. When the variant picker changes the variant it fetches
 * the product page (or a section of it) and resolves `productSelect` with that document in
 * `detail.html` (assets/variant-picker.js `fetchUpdatedSection`). Each block finds its own fresh copy
 * there by tag name + `data-block-id`, inside its own section first, and morphs into it, the same way
 * assets/product-price.js does.
 *
 * blocks/artyeto-price-br.liquid is not here: its root is <product-price>, updated by Horizon's
 * assets/product-price.js.
 */

/**
 * @typedef {Object} SelectDetail
 * @property {Document} [html] - Full page (or section) HTML fetched by the variant picker
 * @property {string} [productId] - Product the event belongs to
 * @property {{ id: string }} [newProduct] - Set when a combined listing swapped the product
 * @property {{ id: number | string } | null} [resource] - Selected variant JSON
 */

/**
 * Base class: listens for `productSelect` on the closest section (or dialog, for quick add) and
 * re-reads this block's markup from the fetched HTML.
 *
 * @template {import('@theme/component').Refs} [T=import('@theme/component').Refs]
 * @extends {Component<T>}
 */
class ArtyetoVariantBlock extends Component {
  /** @type {Element | null} */
  #eventTarget = null;

  connectedCallback() {
    super.connectedCallback();
    this.#eventTarget = this.closest('.shopify-section, dialog');
    this.#eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#eventTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#eventTarget = null;
  }

  /**
   * @param {Event} event - ProductSelectEvent from @shopify/events
   */
  #handleProductSelect = (event) => {
    // Swatches inside product cards fire the same event; they are not this product.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const { promise } = /** @type {{ promise?: Promise<{ detail?: SelectDetail }> }} */ (
      /** @type {unknown} */ (event)
    );
    if (!promise) return;

    promise
      .then(({ detail }) => {
        if (!detail) return;

        if (detail.newProduct) {
          this.dataset.productId = String(detail.newProduct.id);
        } else if (detail.productId && String(detail.productId) !== this.dataset.productId) {
          return;
        }

        this.sync(detail);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[artyeto] productSelect promise rejected:', error);
      });
  };

  /**
   * Morphs this block into its fresh copy from the fetched HTML.
   * @param {SelectDetail} detail
   * @returns {boolean} Whether a fresh copy was found.
   */
  sync(detail) {
    const blockId = this.dataset.blockId;
    if (!blockId || !detail.html) return false;

    // Block ids are only unique per section (template JSON keys), so look in the same section first.
    const selector = `${this.localName}[data-block-id="${CSS.escape(blockId)}"]`;
    const sectionId = this.closest('.shopify-section')?.id;
    const freshSection = sectionId ? detail.html.getElementById(sectionId) : null;
    const fresh = freshSection?.querySelector(selector) ?? detail.html.querySelector(selector);
    if (!fresh) return false;

    morph(this, fresh);
    return true;
  }
}

/**
 * @typedef {Object} LineToggleRefs
 * @property {HTMLAnchorElement} [current] - Link to this product (aria-current)
 * @property {HTMLAnchorElement} [sibling] - Link to the Grau/Sol sibling product
 * @property {HTMLScriptElement} [map] - JSON { [variantId]: siblingVariantUrl }
 */

/**
 * <artyeto-line-toggle>: Grau | Sol links. Keeps the same colour when switching: the Liquid map sends
 * each variant of this product to the sibling variant with the same colour.
 *
 * @extends {ArtyetoVariantBlock<LineToggleRefs>}
 */
class ArtyetoLineToggle extends ArtyetoVariantBlock {
  /**
   * @param {SelectDetail} detail
   * @returns {boolean}
   */
  sync(detail) {
    const variantId = detail.resource?.id;
    const { current, sibling, map } = this.refs;

    if (!detail.newProduct && variantId != null && sibling && map) {
      /** @type {Record<string, string>} */
      let urls = {};
      try {
        urls = JSON.parse(map.textContent || '{}');
      } catch {
        urls = {};
      }

      const siblingUrl = urls[String(variantId)];
      if (siblingUrl) {
        sibling.href = siblingUrl;
        if (current && this.dataset.productUrl) {
          current.href = `${this.dataset.productUrl}?variant=${encodeURIComponent(String(variantId))}`;
        }
        return true;
      }
    }

    return super.sync(detail);
  }
}

/**
 * <artyeto-frame-spec>: "Tamanho M · 47□22 145" with a "Ver medidas" link that opens the accordion
 * row holding <artyeto-frame-measurements> and scrolls to it. Without JS the link is a plain
 * #fragment link (browsers that auto-expand <details> on fragment navigation still open it).
 *
 * @extends {Component}
 */
class ArtyetoFrameSpec extends Component {
  /**
   * @param {MouseEvent} event
   */
  openMeasurements(event) {
    const target = this.#findMeasurements();
    if (!target) return;

    event.preventDefault();

    const details = target.closest('details');
    const summary = details?.querySelector(':scope > summary');

    if (details && !details.open) details.open = true;

    const scrollTarget = details ?? target;
    const focusTarget = summary instanceof HTMLElement ? summary : target;
    if (focusTarget === target && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');

    // Wait one frame so the opened row has its height before scrolling. 'instant', not 'auto': 'auto'
    // follows the computed scroll-behavior, and assets/base.css sets it to smooth on html and .page-wrapper.
    requestAnimationFrame(() => {
      scrollTarget.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'start' });
      focusTarget.focus({ preventScroll: true });
    });
  }

  /**
   * Same dialog (quick add) first, then the same section, then the page outside dialogs.
   * @returns {HTMLElement | null}
   */
  #findMeasurements() {
    const selector = '[data-artyeto-measurements]';
    const scope = this.closest('dialog') ?? this.closest('.shopify-section');
    const inScope = scope?.querySelector(selector);
    if (inScope instanceof HTMLElement) return inScope;

    for (const element of document.querySelectorAll(selector)) {
      if (element instanceof HTMLElement && !element.closest('dialog')) return element;
    }

    return null;
  }
}

if (!customElements.get('artyeto-line-toggle')) customElements.define('artyeto-line-toggle', ArtyetoLineToggle);
if (!customElements.get('artyeto-frame-spec')) customElements.define('artyeto-frame-spec', ArtyetoFrameSpec);
